import { TemplateVariable } from './service';
import { 
  collection, 
  query, 
  where, 
  limit 
} from 'firebase/firestore';
import { db } from '../firebase';
// Plain Firestore read: the registry is system/template data, not
// applicant PII — routing this read through secureApplicationsRepo sent
// it to /api/pii/decrypt, which anchors access on applications/{id} and
// refused every registry id with HTTP 403 (2026-10-26).
import { addDoc, getDocs } from 'firebase/firestore';

/**
 * Migrate hardcoded VARIABLE_GROUPS to Firestore templateVariables collection
 * This should be run once to initialize the variable registry
 */
export async function migrateVariableGroupsToFirestore(): Promise<void> {
  try {
    // Fetch the existing registry so defaults are only ever ADDED (never
    // duplicated). We intentionally do NOT early-return when the collection is
    // non-empty: partial resets/older workspaces may be missing applicant-schema
    // defaults, and calling this again must restore whatever is missing.
    const existingSnapshot = await getDocs(query(collection(db, 'templateVariables')));
    const existingNames = new Set<string>();
    existingSnapshot.forEach((d) => {
      const rawName = d.data()?.name;
      if (typeof rawName === 'string') existingNames.add(rawName);
    });

    
    // Define the variable groups based on the existing VARIABLE_GROUPS in TemplateEditor.tsx
    const variableGroups = [
      {
        label: 'Applicant',
        variables: [
          { name: 'firstName', description: 'First name' },
          { name: 'lastName', description: 'Last name' },
          { name: 'fullName', description: 'Full name' },
          { name: 'email', description: 'Email address' },
          { name: 'phone', description: 'Phone number' },
          { name: 'dateOfBirth', description: 'Date of birth' },
          { name: 'gender', description: 'Gender' },
          { name: 'nationality', description: 'Country of citizenship' },
          { name: 'countryOfBirth', description: 'Country of birth' },
          { name: 'maritalStatus', description: 'Marital status' },
          { name: 'objective', description: 'Objective for participating' },
          { name: 'primaryLanguage', description: 'Primary language' },
          { name: 'otherLanguages', description: 'Other languages spoken' },
          { name: 'computerSkills', description: 'Computer skills' },
          { name: 'countriesResided', description: 'Countries resided in' },
          { name: 'currentCountryOfResidence', description: 'Current country of residence' },
          { name: 'personalSkills', description: 'Personal skills' },
          { name: 'leisureActivities', description: 'Leisure activities / hobbies' },
          { name: 'hospitalityExperience', description: 'Hospitality experience level' },
        ],
      },
      {
        label: 'Address',
        variables: [
          { name: 'address.street', description: 'Street address' },
          { name: 'address.city', description: 'City' },
          { name: 'address.state', description: 'State/province' },
          { name: 'address.zip', description: 'ZIP/postal code' },
          { name: 'address.country', description: 'Country' },
        ],
      },
      {
        label: 'Passport & Visa',
        variables: [
          { name: 'passportNumber', description: 'Passport number' },
          { name: 'passportCountry', description: 'Passport country of issuance' },
          { name: 'passportIssueDate', description: 'Passport issue date' },
          { name: 'passportExpiryDate', description: 'Passport expiration date' },
          { name: 'visaType', description: 'Visa type' },
          { name: 'visaStatus', description: 'Visa status' },
          { name: 'embassyDate', description: 'Embassy appointment date' },
        ],
      },
      {
        label: 'Program & Placement',
        variables: [
          { name: 'programName', description: 'Program name' },
          { name: 'programFee', description: 'Program fee' },
          { name: 'programType', description: 'Program type' },
          { name: 'programDuration', description: 'Program duration' },
          { name: 'programTerm', description: 'Program term / year' },
          { name: 'hostCompany', description: 'Host company/employer' },
          { name: 'hostAddress', description: 'Host company full address' },
          { name: 'hostCity', description: 'Host company city' },
          { name: 'hostState', description: 'Host company state' },
          { name: 'hostCountry', description: 'Host company country' },
        ],
      },
      {
        label: 'Education',
        variables: [
          { name: 'education.0.schoolName', description: 'Most recent — school name' },
          { name: 'education.0.degree', description: 'Most recent — degree' },
          { name: 'education.0.field', description: 'Most recent — field of study' },
          { name: 'education.0.city', description: 'Most recent — city' },
          { name: 'education.0.country', description: 'Most recent — country' },
          { name: 'education.0.startDate', description: 'Most recent — start date' },
          { name: 'education.0.endDate', description: 'Most recent — end date' },
          { name: 'education.0.gpa', description: 'Most recent — GPA' },
          { name: 'education.1.schoolName', description: '2nd most recent — school name' },
          { name: 'education.1.degree', description: '2nd most recent — degree' },
          { name: 'education.1.field', description: '2nd most recent — field' },
          { name: 'education.1.city', description: '2nd most recent — city' },
          { name: 'education.1.country', description: '2nd most recent — country' },
          { name: 'education.1.startDate', description: '2nd most recent — start date' },
          { name: 'education.1.endDate', description: '2nd most recent — end date' },
          { name: 'education.1.gpa', description: '2nd most recent — GPA' },
        ],
      },
      {
        label: 'Employment',
        variables: [
          { name: 'employment.0.companyName', description: 'Most recent — company name' },
          { name: 'employment.0.position', description: 'Most recent — job title' },
          { name: 'employment.0.startDate', description: 'Most recent — start date' },
          { name: 'employment.0.endDate', description: 'Most recent — end date' },
          { name: 'employment.0.city', description: 'Most recent — city' },
          { name: 'employment.0.country', description: 'Most recent — country' },
          { name: 'employment.0.supervisorName', description: 'Most recent — supervisor name' },
          { name: 'employment.1.companyName', description: '2nd most recent — company' },
          { name: 'employment.1.position', description: '2nd most recent — position' },
          { name: 'employment.1.startDate', description: '2nd most recent — start date' },
          { name: 'employment.1.endDate', description: '2nd most recent — end date' },
          { name: 'employment.1.city', description: '2nd most recent — city' },
          { name: 'employment.1.country', description: '2nd most recent — country' },
          { name: 'employment.1.supervisorName', description: '2nd most recent — supervisor name' },
        ],
      },
      {
        label: 'Emergency Contacts',
        variables: [
          { name: 'emergencyName', description: 'Primary emergency contact name' },
          { name: 'emergencyPhone', description: 'Primary emergency contact phone' },
          { name: 'emergencyRelationship', description: 'Primary emergency contact relationship' },
        ],
      },
      {
        label: 'Photo',
        variables: [
          { name: 'photoUrl', description: 'Applicant profile photo URL' },
          { name: 'applicantPhotoUrl', description: 'Applicant profile photo URL (alias)' },
        ],
      },
      {
        label: 'System & Dates',
        variables: [
          { name: 'applicationId', description: 'Application ID' },
          { name: 'today', description: 'Current date (long format)' },
          { name: 'currentDate', description: 'Full current date/time' },
        ],
      },
    ];

    // Map variable names to their data paths and sample values
    const variableMapping: Record<string, { dataPath: string | null; sampleValue: string }> = {
      // Applicant
      firstName: { dataPath: 'personalDetails.firstName', sampleValue: 'John' },
      lastName: { dataPath: 'personalDetails.lastName', sampleValue: 'Doe' },
      fullName: { dataPath: null, sampleValue: 'John Doe' }, // computed
      email: { dataPath: 'personalDetails.email', sampleValue: 'john.doe@example.com' },
      phone: { dataPath: 'personalDetails.phone', sampleValue: '+1 (555) 123-4567' },
      dateOfBirth: { dataPath: 'personalDetails.dob', sampleValue: 'January 15, 1990' },
      gender: { dataPath: 'personalDetails.gender', sampleValue: 'Male' },
      nationality: { dataPath: 'personalDetails.countryOfCitizenship', sampleValue: 'United States' },
      countryOfBirth: { dataPath: 'personalDetails.countryOfBirth', sampleValue: 'United States' },
      maritalStatus: { dataPath: 'personalDetails.maritalStatus', sampleValue: 'Single' },
      objective: { dataPath: 'personalDetails.objective', sampleValue: 'To gain international experience in hospitality management' },
      primaryLanguage: { dataPath: 'personalDetails.primaryLanguage', sampleValue: 'English' },
      otherLanguages: { dataPath: 'personalDetails.otherLanguages', sampleValue: 'Spanish (intermediate), French (basic)' },
      computerSkills: { dataPath: 'personalDetails.computerSkills', sampleValue: 'MS Office, Opera PMS, Salesforce, QuickBooks' },
      countriesResided: { dataPath: 'personalDetails.countriesResided', sampleValue: 'Canada, United Kingdom' },
      currentCountryOfResidence: { dataPath: 'personalDetails.currentCountryOfResidence', sampleValue: 'United States' },
      personalSkills: { dataPath: 'personalDetails.personalSkills', sampleValue: 'Leadership, communication, problem-solving, adaptability' },
      leisureActivities: { dataPath: 'personalDetails.leisureActivities', sampleValue: 'Photography, hiking, playing guitar, reading' },
      hospitalityExperience: { dataPath: 'personalDetails.hospitalityExperience', sampleValue: '3-5 years' },
      
      // Address
      'address.street': { dataPath: 'personalDetails.address.street', sampleValue: '123 Main Street' },
      'address.city': { dataPath: 'personalDetails.address.city', sampleValue: 'New York' },
      'address.state': { dataPath: 'personalDetails.address.state', sampleValue: 'NY' },
      'address.zip': { dataPath: 'personalDetails.address.zip', sampleValue: '10001' },
      'address.country': { dataPath: 'personalDetails.address.country', sampleValue: 'United States' },
      
      // Passport & Visa
      passportNumber: { dataPath: 'passportImmigration.passportNumber', sampleValue: 'AB1234567' },
      passportCountry: { dataPath: 'passportImmigration.passportCountry', sampleValue: 'United States' },
      passportIssueDate: { dataPath: 'passportImmigration.issueDate', sampleValue: 'January 1, 2020' },
      passportExpiryDate: { dataPath: 'passportImmigration.expirationDate', sampleValue: 'January 1, 2030' },
      visaType: { dataPath: 'visaDetails.visaType', sampleValue: 'J-1' },
      visaStatus: { dataPath: 'visaDetails.visaApprovalStatus', sampleValue: 'Approved' },
      embassyDate: { dataPath: 'visaDetails.embassyAppointmentDate', sampleValue: 'March 15, 2026' },
      
      // Program & Placement
      programName: { dataPath: 'assignment.employerName', sampleValue: 'Cultural Exchange Program' }, // fallback logic in context builder
      programFee: { dataPath: 'financialStatus', sampleValue: '$2,500' },
      programType: { dataPath: 'programType', sampleValue: 'J-1 Internship' },
      programDuration: { dataPath: 'programPreferences.programDuration', sampleValue: '12 months' },
      programTerm: { dataPath: 'assignment.programTerm', sampleValue: '2026' },
      hostCompany: { dataPath: 'assignment.employerName', sampleValue: 'Marriott International' },
      hostAddress: { dataPath: null, sampleValue: '456 Innovation Drive, San Francisco, CA 94105' }, // computed
      hostCity: { dataPath: 'assignment.city', sampleValue: 'San Francisco' },
      hostState: { dataPath: 'assignment.state', sampleValue: 'CA' },
      hostCountry: { dataPath: 'assignment.country', sampleValue: 'United States' },
      
      // Education (using first education item)
      'education.0.schoolName': { dataPath: null, sampleValue: 'State University' }, // handled specially in context builder
      'education.0.degree': { dataPath: null, sampleValue: 'Bachelor of Science' },
      'education.0.field': { dataPath: null, sampleValue: 'Hospitality Management' },
      'education.0.city': { dataPath: null, sampleValue: 'Boston' },
      'education.0.country': { dataPath: null, sampleValue: 'United States' },
      'education.0.startDate': { dataPath: null, sampleValue: 'August 2018' },
      'education.0.endDate': { dataPath: null, sampleValue: 'May 2022' },
      'education.0.gpa': { dataPath: null, sampleValue: '3.5' },
      'education.1.schoolName': { dataPath: null, sampleValue: 'Community College' },
      'education.1.degree': { dataPath: null, sampleValue: 'Associate Degree' },
      'education.1.field': { dataPath: null, sampleValue: 'General Studies' },
      'education.1.city': { dataPath: null, sampleValue: 'Chicago' },
      'education.1.country': { dataPath: null, sampleValue: 'United States' },
      'education.1.startDate': { dataPath: null, sampleValue: 'September 2016' },
      'education.1.endDate': { dataPath: null, sampleValue: 'June 2018' },
      'education.1.gpa': { dataPath: null, sampleValue: '3.8' },
      
      // Employment (using first employment item)
      'employment.0.companyName': { dataPath: null, sampleValue: 'Marriott Hotels' },
      'employment.0.position': { dataPath: null, sampleValue: 'Front Desk Associate' },
      'employment.0.startDate': { dataPath: null, sampleValue: 'June 2022' },
      'employment.0.endDate': { dataPath: null, sampleValue: 'Present' },
      'employment.0.city': { dataPath: null, sampleValue: 'New York' },
      'employment.0.country': { dataPath: null, sampleValue: 'United States' },
      'employment.0.supervisorName': { dataPath: null, sampleValue: 'Jane Smith' },
      'employment.1.companyName': { dataPath: null, sampleValue: 'Hilton Hotels' },
      'employment.1.position': { dataPath: null, sampleValue: 'Concierge' },
      'employment.1.startDate': { dataPath: null, sampleValue: 'January 2020' },
      'employment.1.endDate': { dataPath: null, sampleValue: 'May 2022' },
      'employment.1.city': { dataPath: null, sampleValue: 'Chicago' },
      'employment.1.country': { dataPath: null, sampleValue: 'United States' },
      'employment.1.supervisorName': { dataPath: null, sampleValue: 'Bob Johnson' },
      
      // Emergency Contacts
      emergencyName: { dataPath: null, sampleValue: 'Jane Doe' }, // handled specially
      emergencyPhone: { dataPath: null, sampleValue: '+1 (555) 987-6543' },
      emergencyRelationship: { dataPath: null, sampleValue: 'Spouse' },
      
      // Photo
      photoUrl: { dataPath: 'profilePhotoUrl', sampleValue: 'https://placehold.co/200x200' },
      applicantPhotoUrl: { dataPath: 'profilePhotoUrl', sampleValue: 'https://placehold.co/200x200' },
      
      // System & Dates
      applicationId: { dataPath: 'applicationId', sampleValue: 'APP-2026-0001' },
      today: { dataPath: null, sampleValue: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) },
      currentDate: { dataPath: null, sampleValue: new Date().toLocaleString() },
    };

    // Create all variables in Firestore
    const now = new Date().toISOString();
    const createdVariables: string[] = [];
    
    for (const group of variableGroups) {
      for (const variable of group.variables) {
        const mapping = variableMapping[variable.name];
        if (!mapping) {
          console.warn(`No mapping found for variable: ${variable.name}`);
          continue;
        }
        // Restore-defaults semantics: skip variables that already exist so
        // repeated ensure runs stay idempotent.
        if (existingNames.has(variable.name)) continue;
        
        try {
          const docRef = await addDoc(collection(db, 'templateVariables'), {
            name: variable.name,
            label: variable.description,
            group: group.label,
            dataPath: mapping.dataPath,
            sampleValue: mapping.sampleValue,
            deprecated: false,
            createdAt: now,
            updatedAt: now,
          });
          
          createdVariables.push(docRef.id);
        } catch (error) {
          console.error(`Failed to create variable ${variable.name}:`, error);
        }
      }
    }
    
    console.log(`Migration complete: Created ${createdVariables.length} variables in Firestore`);
  } catch (error) {
    console.error('Migration failed:', error);
    throw error;
  }
}